import { shapes } from '@joint/plus';
import CodeIcon from '@mui/icons-material/Code';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Alert, Box, Button, Chip, Collapse, IconButton, Snackbar, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import PlaceholderPicker from '../PropertyPanel/PlaceholderPicker';
import { insertAtCursor } from '../PropertyPanel/insertAtCursor';
import InteractiveListPreview from './InteractiveListPreview';
import { useBaseInspector } from './useBaseInspector';
import { useCellOutputSchema } from './useCellOutputSchema';
import VariableAutocompletePicker from './VariableAutocompletePicker';

/**
 * InteractiveListInspector — Item #7 Fase 2 (08/10/2026 → UX sênior 13/10/2026).
 *
 * Caixa PAUSÁVEL — primeira a estrear a Fundação #0 em prod.
 *
 * UX reformulada (13/10/2026) pra alinhar com NativeAction:
 *   1. Alert "Como usar" narrando o fluxo end-to-end (4 passos)
 *   2. sourceVariable via VariableAutocompletePicker (não mais TextField seco)
 *   3. Chips de tokens disponíveis no itemLabelTemplate (clique insere no cursor)
 *   4. Chips de campos sugeridos no itemValueField (id, uuid, code, email, phone, name, title)
 *   5. Botão {{}} no promptMessage (PlaceholderPicker)
 *   6. **InteractiveListPreview** — renderização visual em tempo real do que vai aparecer no chat
 *   7. Exemplo preenchido (shape JSON esperado) em bloco escuro + copiar
 *   8. Toggle "Ver contrato técnico" colapsável com comportamento detalhado
 *   9. Alert final com portas (selected / error)
 *
 * Admin configura:
 *   - sourceVariable: nome da variável que contém o array (ex.: "meus_chamados")
 *   - itemLabelTemplate: template de rotulagem (ex.: "{{index}}. #{{id}} - {{title}}")
 *   - itemValueField: campo do item que vira a variável de escolha (ex.: "id")
 *   - resultVariable: variável onde a escolha é gravada (ex.: "chamado_selecionado")
 *   - promptMessage: mensagem introdutória (suporta placeholders do fluxo)
 *
 * Portas (fixas, IDs gerados no shape): selected / error.
 * Backend: ChatbotFlowController case FlowTypeInteractiveList → IInteractiveListBoxService.
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

const cellProps = {
    label: ['attrs', 'label', 'text'],
    sourceVariable: ['attrs', 'sourceVariable', 'text'],
    itemLabelTemplate: ['attrs', 'itemLabelTemplate', 'text'],
    itemValueField: ['attrs', 'itemValueField', 'text'],
    resultVariable: ['attrs', 'resultVariable', 'text'],
    promptMessage: ['attrs', 'promptMessage', 'text'],
};

// Campos típicos sugeridos pra itemValueField — chips clicáveis. Ordem = frequência real.
// `url` adicionado 13/10/2026 após o enriquecimento do CalledListAttachmentsHandler expor
// url pronta por item (fluxo "InteractiveList sobre anexos" fica 1-clique).
const VALUE_FIELD_SUGGESTIONS = ['id', 'url', 'uuid', 'code', 'email', 'phone', 'name', 'title'];

// Shape JSON esperado da variável fonte — exemplo didático.
const EXAMPLE_ARRAY_JSON = `[
  { "id": 42, "title": "Problema no login" },
  { "id": 43, "title": "Dúvida sobre fatura" },
  { "id": 44, "title": "Acesso remoto" }
]`;

const InteractiveListInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [sourceVariable, setSourceVariable] = useState<string>('');
    const [itemLabelTemplate, setItemLabelTemplate] = useState<string>('{{index}}. {{title}}');
    const [itemValueField, setItemValueField] = useState<string>('id');
    const [resultVariable, setResultVariable] = useState<string>('');
    const [promptMessage, setPromptMessage] = useState<string>('Escolha uma opção:');

    const [contractOpen, setContractOpen] = useState<boolean>(false);
    const [copied, setCopied] = useState<boolean>(false);

    // 13/10/2026 — Inferência downstream. Dado o nome em sourceVariable, traversa o grafo,
    // detecta a NativeAction upstream que grava essa var, puxa output_schema do catálogo.
    // → itemFields é usado pra enriquecer os chips estáticos de VALUE_FIELD_SUGGESTIONS e
    //   os tokens genéricos do itemLabelTemplate com os CAMPOS REAIS do array da upstream.
    const upstream = useCellOutputSchema({ variableName: sourceVariable, cell });

    // Chips de itemValueField: fields detectados vêm PRIMEIRO (específicos), seguido dos
    // genéricos que não colidam. Sem detecção, cai nos estáticos.
    const dynamicValueFields = upstream.isArray ? upstream.itemFields.map((f) => f.name) : [];
    const valueFieldChips = dynamicValueFields.length > 0
        ? [...new Set([...dynamicValueFields, ...VALUE_FIELD_SUGGESTIONS.filter((f) => !dynamicValueFields.includes(f))])]
        : VALUE_FIELD_SUGGESTIONS;

    // Tokens de itemLabelTemplate: `{{index}}` sempre + fields detectados como `{{<campo>}}` +
    // fallback nos genéricos se nada detectado.
    const dynamicTokens = upstream.isArray
        ? upstream.itemFields.map((f) => `{{${f.name}}}`)
        : [];
    const templateTokens = ['{{index}}', ...(
        dynamicTokens.length > 0
            ? dynamicTokens
            : ['{{id}}', '{{title}}', '{{name}}', '{{description}}', '{{code}}']
    )];

    // Mapa campo → title pra tooltip dos chips (hint humano).
    const fieldTitles = new Map<string, string>();
    upstream.itemFields.forEach((f) => { if (f.title) fieldTitles.set(f.name, f.title); });

    // Picker de placeholder — usado no promptMessage.
    const [pickerAnchor, setPickerAnchor] = useState<HTMLElement | null>(null);
    const activeInputRef = useRef<HTMLInputElement | null>(null);

    // Refs específicos pra inserir token no template clicando num chip.
    const templateInputRef = useRef<HTMLInputElement | null>(null);
    const valueFieldInputRef = useRef<HTMLInputElement | null>(null);

    const isSyncingRef = useRef(false);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;
        setLabel(cell.prop(cellProps.label) ?? '');
        setSourceVariable(cell.prop(cellProps.sourceVariable) ?? '');
        setItemLabelTemplate(cell.prop(cellProps.itemLabelTemplate) ?? '{{index}}. {{title}}');
        setItemValueField(cell.prop(cellProps.itemValueField) ?? 'id');
        setResultVariable(cell.prop(cellProps.resultVariable) ?? '');
        setPromptMessage(cell.prop(cellProps.promptMessage) ?? 'Escolha uma opção:');
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persist = (path: string[], value: string) => {
        isSyncingRef.current = true;
        try { changeCellProp(path, value); }
        finally { queueMicrotask(() => { isSyncingRef.current = false; }); }
    };

    // Insere token no template no cursor (chip clicável).
    const insertTokenInTemplate = (token: string) => {
        const input = templateInputRef.current;
        if (!input) {
            // Fallback: concatena no fim.
            const next = itemLabelTemplate + token;
            setItemLabelTemplate(next);
            persist(cellProps.itemLabelTemplate, next);
            return;
        }
        insertAtCursor(input, token);
        const next = input.value;
        setItemLabelTemplate(next);
        persist(cellProps.itemLabelTemplate, next);
    };

    // Insere sugestão no valueField (chip clicável = substitui o valor atual).
    const applyValueFieldSuggestion = (field: string) => {
        setItemValueField(field);
        persist(cellProps.itemValueField, field);
    };

    // Insere placeholder do fluxo no promptMessage via PlaceholderPicker.
    const openPicker = (anchor: HTMLElement, input: HTMLInputElement | null) => {
        activeInputRef.current = input;
        setPickerAnchor(anchor);
    };
    const onPlaceholderInsert = (placeholder: string) => {
        const input = activeInputRef.current;
        if (!input) { setPickerAnchor(null); return; }
        insertAtCursor(input, placeholder);
        const next = input.value;
        setPromptMessage(next);
        persist(cellProps.promptMessage, next);
        setPickerAnchor(null);
    };

    const copyExample = () => {
        navigator.clipboard?.writeText(EXAMPLE_ARRAY_JSON).then(() => setCopied(true)).catch(() => {});
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>

                {/* ===== Cabeçalho ===== */}
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'il.title', defaultMessage: 'Lista Interativa' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'il.title.hint',
                        defaultMessage: 'Renderiza um array (de uma variável) como lista numerada no chat, pausa o fluxo e aguarda a escolha do cliente.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                {/* ===== Alert "Como usar" (narrativa end-to-end) ===== */}
                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ fontSize: 12, py: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                        {intl.formatMessage({ id: 'il.howto.title', defaultMessage: 'Como esta caixa funciona:' })}
                    </Typography>
                    <Typography variant="caption" component="div" sx={{ lineHeight: 1.6 }}>
                        {intl.formatMessage({
                            id: 'il.howto.steps',
                            defaultMessage: '1) Uma caixa anterior gera um ARRAY (ex.: NativeAction "called.list_by_user") e grava numa variável. 2) Esta caixa renderiza esse array como lista numerada no chat. 3) Cliente responde com o número ou texto. 4) A caixa grava o campo configurado na variável de destino e segue pela porta "Cliente escolheu".',
                        })}
                    </Typography>
                </Alert>

                <TextField
                    label={intl.formatMessage({ id: 'il.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder="Lista Interativa"
                />

                {/* ===== 1. Variável de origem (array) ===== */}
                <VariableAutocompletePicker
                    value={sourceVariable}
                    onChange={(name) => {
                        setSourceVariable(name);
                        persist(cellProps.sourceVariable, name);
                    }}
                    label={intl.formatMessage({ id: 'il.sourceVariable', defaultMessage: '1. Variável de origem (array) *' })}
                    placeholder={intl.formatMessage({
                        id: 'il.sourceVariable.placeholder',
                        defaultMessage: 'Escolha a variável que contém o array',
                    })}
                    helperText={intl.formatMessage({
                        id: 'il.sourceVariable.help',
                        defaultMessage: 'Variável criada por caixa anterior (ex.: NativeAction called.list_by_user → "meus_chamados").',
                    })}
                />

                {/* Detecção automática do shape upstream — mostra qual ação enriquece essa var. */}
                {upstream.slug && upstream.isArray && upstream.itemFields.length > 0 && (
                    <Alert severity="success" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ fontSize: 12, py: 0.5 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>
                            {intl.formatMessage({
                                id: 'il.upstream.detected',
                                defaultMessage: '✨ Detectei que esta variável vem de "{action}" — campos sugeridos abaixo foram ajustados para o shape real:',
                            }, { action: upstream.displayName ?? upstream.slug })}
                        </Typography>
                    </Alert>
                )}

                {/* ===== 2. Mensagem antes da lista ===== */}
                <TextField
                    label={intl.formatMessage({ id: 'il.promptMessage', defaultMessage: '2. Mensagem antes da lista' })}
                    size="small"
                    multiline
                    minRows={2}
                    maxRows={5}
                    value={promptMessage}
                    onChange={(e) => {
                        setPromptMessage(e.target.value);
                        persist(cellProps.promptMessage, e.target.value);
                    }}
                    placeholder="ex.: Qual chamado deseja visualizar?"
                    helperText={intl.formatMessage({
                        id: 'il.promptMessage.help',
                        defaultMessage: 'Mensagem exibida antes da lista. Suporta placeholders do fluxo ({{variable}}, %param%, @legacy@, $cf$).',
                    })}
                    inputRef={(el) => { /* lastRef pro picker — gerenciado via activeInputRef */ }}
                    InputProps={{
                        endAdornment: (
                            <Tooltip title={intl.formatMessage({ id: 'il.pickPlaceholder', defaultMessage: 'Inserir variável/placeholder' })}>
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        // Acha o textarea dentro do TextField (multiline).
                                        const node = e.currentTarget.closest('.MuiFormControl-root')?.querySelector('textarea, input') as HTMLInputElement | null;
                                        openPicker(e.currentTarget as HTMLElement, node);
                                    }}
                                    sx={{ p: 0.25 }}
                                >
                                    <CodeIcon fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        ),
                    }}
                />

                {/* ===== 3. Template de cada item ===== */}
                <Box>
                    <TextField
                        label={intl.formatMessage({ id: 'il.itemLabelTemplate', defaultMessage: '3. Template de cada item' })}
                        size="small"
                        fullWidth
                        value={itemLabelTemplate}
                        onChange={(e) => {
                            setItemLabelTemplate(e.target.value);
                            persist(cellProps.itemLabelTemplate, e.target.value);
                        }}
                        placeholder="{{index}}. {{title}}"
                        inputRef={(el) => { templateInputRef.current = el; }}
                        helperText={intl.formatMessage({
                            id: 'il.itemLabelTemplate.help',
                            defaultMessage: 'Modelo de rotulagem. {{index}} = número 1-based. {{<campo>}} = valor de um campo do item.',
                        })}
                    />
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                            {intl.formatMessage({ id: 'il.tokens.label', defaultMessage: 'Inserir token:' })}
                        </Typography>
                        {templateTokens.map((tok) => {
                            // Chip com título (tooltip) quando a field tem title no schema upstream.
                            const bare = tok.replace(/^\{\{|\}\}$/g, '');
                            const title = fieldTitles.get(bare);
                            const chip = (
                                <Chip
                                    key={tok}
                                    label={tok}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => insertTokenInTemplate(tok)}
                                    sx={{ height: 20, fontSize: 10, fontFamily: 'monospace', cursor: 'pointer' }}
                                />
                            );
                            return title ? <Tooltip key={tok} title={title}>{chip}</Tooltip> : chip;
                        })}
                    </Stack>
                </Box>

                {/* ===== Prévia visual (componente reusável) ===== */}
                <InteractiveListPreview
                    promptMessage={promptMessage}
                    itemLabelTemplate={itemLabelTemplate}
                />

                {/* ===== 4. Campo que vira variável de escolha ===== */}
                <Box>
                    <TextField
                        label={intl.formatMessage({ id: 'il.itemValueField', defaultMessage: '4. Campo que vira variável de escolha' })}
                        size="small"
                        fullWidth
                        value={itemValueField}
                        onChange={(e) => {
                            setItemValueField(e.target.value);
                            persist(cellProps.itemValueField, e.target.value);
                        }}
                        placeholder="id"
                        inputRef={(el) => { valueFieldInputRef.current = el; }}
                        helperText={intl.formatMessage({
                            id: 'il.itemValueField.help',
                            defaultMessage: 'Qual CAMPO do item escolhido vira o valor gravado na variável de destino. Dot-notation suportada (ex.: "customer.id").',
                        })}
                    />
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography variant="caption" sx={{ color: 'text.secondary', mr: 0.5 }}>
                            {intl.formatMessage({ id: 'il.valueFieldSuggestions.label', defaultMessage: 'Sugestões:' })}
                        </Typography>
                        {valueFieldChips.map((f) => {
                            const title = fieldTitles.get(f);
                            const chip = (
                                <Chip
                                    key={f}
                                    label={f}
                                    size="small"
                                    variant={itemValueField === f ? 'filled' : 'outlined'}
                                    color={itemValueField === f ? 'primary' : 'default'}
                                    onClick={() => applyValueFieldSuggestion(f)}
                                    sx={{ height: 20, fontSize: 10, fontFamily: 'monospace', cursor: 'pointer' }}
                                />
                            );
                            return title ? <Tooltip key={f} title={title}>{chip}</Tooltip> : chip;
                        })}
                    </Stack>
                </Box>

                {/* ===== 5. Variável de destino (resultVariable) — já via picker ===== */}
                <VariableAutocompletePicker
                    value={resultVariable}
                    onChange={(name) => {
                        setResultVariable(name);
                        persist(cellProps.resultVariable, name);
                    }}
                    label={intl.formatMessage({ id: 'il.resultVariable', defaultMessage: '5. Variável de destino (escolha do cliente)' })}
                    helperText={intl.formatMessage({
                        id: 'il.resultVariable.help',
                        defaultMessage: 'Variável onde o campo do item escolhido é gravado — próximas caixas usam com @nome@ ou {{variable.nome}}.',
                    })}
                />

                {/* ===== Exemplo preenchido (shape JSON esperado) ===== */}
                <Box>
                    <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ fontSize: 12, py: 0.5, mb: 0.5 }}>
                        {intl.formatMessage({
                            id: 'il.example.help',
                            defaultMessage: 'Formato esperado da variável de origem (array de objetos). Copie e adapte conforme o retorno da sua caixa anterior.',
                        })}
                    </Alert>
                    <Box sx={{ background: '#1e1e2e', color: '#e0e0e0', fontFamily: 'monospace', fontSize: 11, p: 1, borderRadius: 1 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                            <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 500 }}>
                                {intl.formatMessage({ id: 'il.example.label', defaultMessage: 'Exemplo do array esperado:' })}
                            </Typography>
                            <Tooltip title={intl.formatMessage({ id: 'il.example.copy', defaultMessage: 'Copiar exemplo' })}>
                                <IconButton size="small" onClick={copyExample} sx={{ color: '#e0e0e0', p: 0.25 }}>
                                    <ContentCopyIcon fontSize="inherit" sx={{ fontSize: 14 }} />
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        <pre
                            style={{
                                margin: 0,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                userSelect: 'text',
                                WebkitUserSelect: 'text',
                                cursor: 'text',
                            }}
                        >
                            {EXAMPLE_ARRAY_JSON}
                        </pre>
                    </Box>
                </Box>

                {/* ===== Toggle "Ver contrato técnico" ===== */}
                <Box>
                    <Button
                        size="small"
                        variant="text"
                        startIcon={contractOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        endIcon={<CodeIcon fontSize="small" />}
                        onClick={() => setContractOpen((v) => !v)}
                        sx={{ textTransform: 'none', color: 'text.secondary' }}
                    >
                        {contractOpen
                            ? intl.formatMessage({ id: 'il.contract.hide', defaultMessage: 'Esconder contrato técnico' })
                            : intl.formatMessage({ id: 'il.contract.show', defaultMessage: 'Ver contrato técnico (comportamento)' })}
                    </Button>
                    <Collapse in={contractOpen}>
                        <Box sx={{
                            background: '#1e1e2e',
                            color: '#e0e0e0',
                            fontFamily: 'monospace',
                            fontSize: 11,
                            p: 1,
                            borderRadius: 1,
                            lineHeight: 1.6,
                        }}>
                            <Typography variant="caption" sx={{ color: '#e0e0e0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                                {intl.formatMessage({ id: 'il.contract.label', defaultMessage: 'Comportamento técnico:' })}
                            </Typography>
                            <Typography variant="caption" component="div" sx={{ color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>
                                {intl.formatMessage({
                                    id: 'il.contract.body',
                                    defaultMessage: '• A variável de origem DEVE conter um array de objetos JSON.\n• {{index}} é interpolado como número 1-based (1, 2, 3, …).\n• {{<campo>}} aceita dot-notation (ex.: {{customer.name}}).\n• Campo ausente num item vira string vazia (não exibe "undefined").\n• Array vazio ou variável inválida → fluxo sai pela porta "Erro".\n• Cliente escolhe digitando o número (1, 2, …) OU o texto exato de um item.\n• Após a escolha, o valor do campo configurado é gravado na variável de destino.\n• A caixa é PAUSÁVEL — fluxo pausa até cliente responder (sem timeout no momento).',
                                })}
                            </Typography>
                        </Box>
                    </Collapse>
                </Box>

                {/* ===== Alert de portas (mantido do original) ===== */}
                <Alert severity="info" sx={{ fontSize: 12, py: 0.5 }}>
                    {intl.formatMessage({
                        id: 'il.ports.hint',
                        defaultMessage: 'Esta caixa é PAUSÁVEL. Fluxo para aqui aguardando resposta do cliente (sem timeout — espera indefinidamente). 2 saídas fixas: "Cliente escolheu" e "Erro".',
                    })}
                </Alert>
            </Box>

            <PlaceholderPicker
                open={!!pickerAnchor}
                anchorEl={pickerAnchor}
                onClose={() => setPickerAnchor(null)}
                onInsert={onPlaceholderInsert}
            />

            <Snackbar
                open={copied}
                autoHideDuration={1800}
                onClose={() => setCopied(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                message={intl.formatMessage({ id: 'il.copied', defaultMessage: 'Copiado!' })}
            />
        </>
    );
};

export default InteractiveListInspector;
